// Shared by web + native onboarding (plan-building chips, habit suggestions,
// coach picker, night-owl day start). Pure TS.

import type { CoachVoice } from './coach'
import { DEFAULT_DAY_START_HOUR } from './day'
import { type GoalDeadline, deadlineFromGoal, withoutDeadline } from './goalDeadline'
import type { RecursPeriod } from './schedule'
import { type TimeBudgetChoice, budgetForChoice, timeBudgetEffort } from './timeBudget'

export const PLAN_LEVELS = ['beginner', 'some', 'experienced'] as const
export const PLAN_DEADLINES = ['1m', '2m', '3m', '4m', '5m', '6m', '9m', '12m', 'none'] as const
// Native asks the deadline as chips, so fewer of them.
export const PLAN_DEADLINE_CHIPS = ['1m', '3m', '6m', '12m', 'none'] as const
export const PLAN_DAY_PARTS = ['morning', 'midday', 'evening'] as const
export const PLAN_TIMES = ['15min', '30min', '1hr', '2hrPlus'] as const

export type PlanLevel = (typeof PLAN_LEVELS)[number]
export type PlanDeadline = (typeof PLAN_DEADLINES)[number]
export type PlanTime = (typeof PLAN_TIMES)[number]
export type PlanDayPart = (typeof PLAN_DAY_PARTS)[number]
export type GoalStartMode = 'now' | 'date' | 'unsure'
// 'goal': the deadline the goal text itself names ("by March").
export type PlanDeadlineAnswer = PlanDeadline | 'goal'
export type PlanAnswers = {
  level?: PlanLevel
  deadline?: PlanDeadlineAnswer
  time?: PlanTime
  budget?: TimeBudgetChoice
  // "Which days work?" (0 = Sun … 6 = Sat) and when in the day; both optional.
  days?: number[]
  dayPart?: PlanDayPart
  startMode?: GoalStartMode
  // Local calendar date, YYYY-MM-DD. Only used when startMode is `date`.
  startDate?: string
}

export const DAY_PART_CLOCK: Record<PlanDayPart, string> = {
  morning: '08:00',
  midday: '12:00',
  evening: '19:00',
}

const LEVEL_PROMPT: Record<PlanLevel, string> = {
  beginner: 'Complete beginner',
  some: 'Some experience',
  experienced: 'Fairly experienced',
}
const DEADLINE_MONTHS: Record<Exclude<PlanDeadline, 'none'>, number> = {
  '1m': 1,
  '2m': 2,
  '3m': 3,
  '4m': 4,
  '5m': 5,
  '6m': 6,
  '9m': 9,
  '12m': 12,
}
const TIME_EFFORT: Record<PlanTime, 'low' | 'medium' | 'high'> = {
  '15min': 'low',
  '30min': 'medium',
  '1hr': 'medium',
  '2hrPlus': 'high',
}

export const DEFAULT_PLAN_ANSWERS: Required<Pick<PlanAnswers, 'level' | 'deadline' | 'time'>> = {
  level: 'some',
  deadline: '3m',
  time: '30min',
}

const DAY = 86_400_000

// Keep AI-generated relative deadlines anchored to the selected start day.
export function planStartTimestamp(answers: PlanAnswers, now = Date.now()) {
  if (answers.startMode !== 'date' || !answers.startDate) return now
  const date = new Date(now)
  const today = [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
  const days = Math.round(
    (Date.parse(`${answers.startDate}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY,
  )
  return now + Math.max(0, days) * DAY
}

export function deadlineDate(
  deadline: PlanDeadlineAnswer,
  now = Date.now(),
  goal?: GoalDeadline | null,
): number | undefined {
  if (deadline === 'goal') return goal?.dueDate ?? deadlineDate(DEFAULT_PLAN_ANSWERS.deadline, now)
  return deadline === 'none' ? undefined : now + DEADLINE_MONTHS[deadline] * 30 * DAY
}

// Slider stops, with the goal's own deadline slotted in by date.
export function deadlineOptions(
  goal: GoalDeadline | null | undefined,
  now = Date.now(),
  options: readonly PlanDeadline[] = PLAN_DEADLINES,
): PlanDeadlineAnswer[] {
  if (!goal) return [...options]
  const at = options.findIndex((o) => (deadlineDate(o, now) ?? Infinity) > goal.dueDate)
  const out: PlanDeadlineAnswer[] = [...options]
  out.splice(at < 0 ? out.length : at, 0, 'goal')
  return out
}

// Unanswered, a goal that names its deadline starts there.
export function deadlineAnswer(answers: PlanAnswers, goal?: GoalDeadline | null) {
  return answers.deadline === 'goal' && !goal
    ? DEFAULT_PLAN_ANSWERS.deadline
    : (answers.deadline ?? (goal ? 'goal' : DEFAULT_PLAN_ANSWERS.deadline))
}

// decomposeGoal args for the chip answers; unanswered/skipped chips use defaults.
// A time budget ("How much time can you give this?") overrides the per-day time.
export function planInputs(answers: PlanAnswers, now = Date.now(), goal?: GoalDeadline | null) {
  const { level, time } = { ...DEFAULT_PLAN_ANSWERS, ...answers }
  const timeBudget = budgetForChoice(answers.budget)
  return {
    dueDate: deadlineDate(deadlineAnswer(answers, goal), now, goal),
    experienceLevel: LEVEL_PROMPT[level],
    effortLevel: timeBudget ? timeBudgetEffort(timeBudget) : TIME_EFFORT[time],
    timeAvailable: timeBudget ? planTimeFor(timeBudget.minutesPerWeek) : time,
    ...(timeBudget && { timeBudget }),
    ...(answers.days?.length && { scheduleDays: [...answers.days].sort((a, b) => a - b) }),
    ...(answers.dayPart && { dayPart: answers.dayPart }),
  }
}

// The goal text + args for a plan. A deadline phrase in the goal ("by March")
// is dropped when another deadline was picked, so the title can't contradict it.
export function planRequest(
  goalText: string,
  answers: PlanAnswers,
  { now = Date.now(), locale }: { now?: number; locale?: string } = {},
) {
  const goal = deadlineFromGoal(goalText, now, locale)
  const keep = !goal || deadlineAnswer(answers, goal) === 'goal'
  return {
    goalText: keep ? goalText.trim() : withoutDeadline(goalText.trim(), goal.phrase),
    ...planInputs(answers, now, goal),
  }
}

type PlanHabitShape = { cadence: RecursPeriod; days?: number[]; time?: string }

// Holds a generated plan to the answers: habits on the chosen days and time of
// day, and no stale deadline phrase in the title.
export function fitPlan<T extends { title: string; habits?: PlanHabitShape[] }>(
  tree: T,
  goalText: string,
  answers: PlanAnswers,
  { now = Date.now(), locale }: { now?: number; locale?: string } = {},
): T {
  const goal = deadlineFromGoal(goalText, now, locale)
  const title =
    goal && deadlineAnswer(answers, goal) !== 'goal'
      ? withoutDeadline(tree.title, goal.phrase)
      : tree.title
  const days = answers.days?.length ? [...answers.days].sort((a, b) => a - b) : null
  const time = answers.dayPart ? DAY_PART_CLOCK[answers.dayPart] : undefined
  const habits = tree.habits?.map((h) => {
    let next = h
    if (days && h.cadence !== 'weekly' && h.cadence !== 'monthly') {
      const fit = h.cadence === 'days' ? (h.days ?? []).filter((d) => days.includes(d)) : []
      next = { ...next, cadence: 'days', days: fit.length ? fit : days }
    }
    return time ? { ...next, time } : next
  })
  return { ...tree, title, ...(habits && { habits }) } as T
}

// Nearest per-day chip for a weekly budget (profile + coach step still use it).
export function planTimeFor(minutesPerWeek: number): PlanTime {
  const daily = minutesPerWeek / 7
  return daily <= 15 ? '15min' : daily <= 30 ? '30min' : daily <= 60 ? '1hr' : '2hrPlus'
}

const TIME_PROMPT: Record<string, string> = {
  '15min': '15 minutes',
  '30min': '30 minutes',
  '1hr': '1 hour',
  '2hrPlus': '2+ hours',
}

// timeAvailable is stored as a chip key; spell it out for the prompt.
export function timeAvailablePrompt(time: string): string {
  return TIME_PROMPT[time] ?? time
}

export const ONBOARDING_HABIT_LIMIT = 4
export const ONBOARDING_HABITS_PRETICKED = 2

// Most-picked habits overall, best first; onboarding preticks the top ones the
// user doesn't already track, whatever the goal's category.
export const MOST_POPULAR_HABITS = [
  'waterWake',
  'walk',
  'readPages',
  'meditate',
  'sleep',
  'stretch',
]

export function defaultHabitPicks<T extends { key: string }>(
  pool: readonly T[],
  n = ONBOARDING_HABITS_PRETICKED,
): T[] {
  return MOST_POPULAR_HABITS.flatMap((k) => pool.filter((h) => h.key === k)).slice(0, n)
}

// Habits matching the goal's category (habit categories share the goal
// category names), else one per category for a goal-less or unknown category.
export function suggestHabits<T extends { category: string }>(
  habits: readonly T[],
  goalCategory?: string | null,
  limit = ONBOARDING_HABIT_LIMIT,
): T[] {
  const cat = goalCategory?.toLowerCase()
  const matching = habits.filter((h) => h.category === cat)
  if (matching.length) return matching.slice(0, limit)
  const seen = new Set<string>()
  return habits.filter((h) => !seen.has(h.category) && seen.add(h.category)).slice(0, limit)
}

// Onboarding picks the voice only: the style is left unset so it keeps adapting
// to how the user is doing. `line` just keys the sample quote's tone.
export const ONBOARDING_COACHES: { voice: CoachVoice; line: 'strict' | 'warm' | 'hype' }[] = [
  { voice: 'leo', line: 'strict' },
  { voice: 'ara', line: 'warm' },
  { voice: 'eve', line: 'hype' },
]

// The night-owl toggle pushes the rollover to 6am (the Settings max) so
// after-midnight check-ins still count for the day before.
export const NIGHT_OWL_DAY_START_HOUR = 6

export function dayStartHourFor(nightOwl: boolean): number {
  return nightOwl ? NIGHT_OWL_DAY_START_HOUR : DEFAULT_DAY_START_HOUR
}
